/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as fromAppRoot from '@app/app.reducers';

import { TrackingDetail } from '@shared/models/CustomerAccount';
import { getCookie, removeCookie } from '@shared/utils';
import { environment } from 'src/environments/environment';

declare let fbq: any; // for pixel tracking
declare let dataLayer: any; // for GTM / UTT tracking
declare let gtag: any; // for old Google Analytics
declare let qp: any; // for Quora pixel

@Injectable({
  providedIn: 'root'
})
export class TrackingService implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  currentAccountID: string;
  currentLocation: string;
  lastCustomReferrer = '';
  referrer: string;
  trackId: string;
  appLocation: string;
  isCIActive = false;

  constructor(
    private http: HttpClient,
    private store: Store
  ) {
    this.store.pipe(select(fromAppRoot.selectCustomerAndCurrentAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      if (!value || !value.customer || !value.customer.email || !value.currentAccountDetail) {
        return;
      }

      this.trackId = value.customer.track_id;
      this.appLocation = value.customer.app_location;
      this.currentAccountID = value.currentAccountDetail.id;

      this.isCIActive = !value.customer.accounts.length && value.customer.has_crowd_investor_account;
    });
  }

  updateLocation(referrer: string, currentLocation: string): void {
    this.referrer = referrer;
    this.currentLocation = currentLocation;
  }

  private getTrackingObject(type: string, pageView: string, trackPureActivityName = false) {
    this.isCIActive = localStorage.getItem('CIAccount') ? true : false;

    const trackingDetails = {
      account_id: this.isCIActive ? '' : this.currentAccountID || '',
      content: '',
      location: this.currentLocation,
      platform: 'web-app',
      referrer: this.referrer
    };

    if (type === 'View') {
      trackingDetails.content = this.isCIActive ? `CI: Page visited: ${pageView}` : `Page visited: ${pageView}`;
    } else {
      const finalActivity = this.isCIActive ? `CI: Activity clicked: ${pageView}` : `Activity clicked: ${pageView}`;
      trackingDetails.content = trackPureActivityName ? pageView : finalActivity;
    }

    return trackingDetails;
  }

  trackView(pageView: string): void {
    const trackingDetails = this.getTrackingObject('View', pageView);
    this.trackJourney(trackingDetails);
  }

  trackActivity(activity: string, trackPureActivityName?: boolean): void {
    const trackingDetails = this.getTrackingObject('Activity', activity, trackPureActivityName);
    this.lastCustomReferrer = this.currentLocation;
    this.trackJourney(trackingDetails);
  }

  trackJourney(trackingDetails: TrackingDetail): void {
    if (environment.name === 'development') {
      // eslint-disable-next-line no-console
      console.log(`[Lead activity] ${trackingDetails.content}`, trackingDetails);
    }
    this.http.post(`${environment.apiUrl}/journey/action`, trackingDetails).subscribe(
      () => {},
      () => {}
    );
  }

  trackKeyword(searchKeyword: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/accounts/${this.currentAccountID}/strategy/company/search/track`, {
      q: searchKeyword
    });
  }

  trackLastTouchPoint(): void {
    const yovaSource = document.cookie.match(RegExp('(?:^|;\\s*)_yova_source=([^;]*)'));
    const sti = yovaSource !== null ? getCookie('_yova_source') : '';
    if (sti.length > 0) {
      this.http.post(`${environment.apiUrl}/track_touch_point`, { source_type_id: sti }).subscribe(
        () => {
          removeCookie('_yova_source');
        },
        () => {}
      );
    }
  }

  setSTIFromURL(fromRegister = false): void {
    let sti: string;
    const stiPresent = document.cookie.match(RegExp('(?:^|;\\s*)_yova_source=([^;]*)'));

    if (document.URL.includes('sti=')) {
      const urlParams = new URLSearchParams(window.location.search);
      sti = urlParams.get('sti');

      if (sti && sti.length) {
        document.cookie = `_yova_source=${sti}`;
      } else if (fromRegister && !stiPresent) {
        document.cookie = '_yova_source=cf91c14dec'; // production pure code is cf91c14dec
      }
    }
  }

  trackIdVerificationStatus(action): void {
    this.http
      .put<{ data: { attributes: { status } } }>(`${environment.apiUrlV3}/accounts/${this.currentAccountID}/idnow_identification`, {
        data: {
          id: 0,
          type: 'identifications',
          attributes: {
            status: action
          }
        }
      })
      .subscribe();
  }

  // Track page visit on every page
  trackAppNavigation(event: NavigationEnd): void {
    this.referrer = this.currentLocation ?? 'unknown';
    this.currentLocation = event.url;
    this.updateLocation(this.referrer, this.currentLocation);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
